import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { setupApi, type City } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const QK = ['setup', 'cities'] as const;

interface FormShape {
  name: string;
  area?: string;
}

export function CitiesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('SETUP:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('SETUP:EDIT'));

  const [editing, setEditing] = useState<City | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: () => setupApi.listCities(false),
  });

  const q = search.trim().toLowerCase();
  const filtered = !q
    ? data ?? []
    : (data ?? []).filter(
        (c) => c.name.toLowerCase().includes(q) || (c.area && c.area.toLowerCase().includes(q)),
      );

  const upsert = useMutation({
    mutationFn: async (values: FormShape) => {
      const body = { name: values.name.trim(), area: values.area?.trim() || null };
      return editing ? setupApi.updateCity(editing.id, body) : setupApi.createCity(body);
    },
    onSuccess: () => {
      message.success(editing ? 'City updated' : 'City created');
      qc.invalidateQueries({ queryKey: QK });
      setOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => setupApi.toggleCity(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row: City) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name, area: row.area ?? '' });
    setOpen(true);
  };

  const columns: TableProps<City>['columns'] = [
    {
      title: 'City',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
    },
    {
      title: 'Area',
      dataIndex: 'area',
      render: (a: string | null) =>
        a ? <span>{a}</span> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit || (toggle.isPending && toggle.variables?.id === row.id)}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / General"
        title="Cities"
        description="Address book entries used by student admissions and staff records."
        count={{ value: data?.length ?? 0, label: 'cities' }}
        actions={
          <Space>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search city or area"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New city
              </Button>
            )}
          </Space>
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New city'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => upsert.mutate(values)}
        >
          <Form.Item
            label="City"
            name="name"
            rules={[
              { required: true, message: 'City is required' },
              { max: 100 },
            ]}
          >
            <Input placeholder="e.g. Sialkot" autoFocus />
          </Form.Item>
          <Form.Item label="Area" name="area" rules={[{ max: 100 }]}>
            <Input placeholder="Optional, e.g. Cantt" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
